import { SegmentedButtons } from '@scale/scaleui';
import { isScaleEmail } from '@scale/shared-types';
import { AuditGradeV2, AuditResultV2, AuditResultV2Old } from '@scale/shared/defaults';
import { IUserJson } from '@scale/shared/types/user';
import { Spinner, TextInput } from 'design-system';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import {
  SpeedAuditLookup,
  SubtaskAssignment,
} from 'react-scale/components/audits/SpeedAudit/types';
import SpeedAuditErrorPicker from 'react-scale/components/audits/SpeedAudit/v2/SpeedAuditErrorPicker';
import useSpeedAuditFeedback from 'react-scale/components/audits/SpeedAudit/v2/hooks/useSpeedAuditFeedback';
import { StellarTaskDialog } from './StellarTaskDialog';
interface SpeedAuditFeedbackProps {
  projectId: string;
  attemptAuditId: string;
  objectId: string; // subtask, taskreview,etc
  attemptId: string; // taskreview, taskattempt, etc.
  subtaskAssignment?: SubtaskAssignment;
  triggerShortcut?: boolean;
  triggerShortcutValue?: AuditResultV2;
  onAuditUpdate?: (audit: SpeedAuditLookup) => void;
  user?: IUserJson;
  setIsWritingFeedback?: (isWriting: boolean) => void;
}

const SpeedAuditFeedback: React.FC<SpeedAuditFeedbackProps> = ({
  attemptAuditId,
  objectId,
  attemptId,
  projectId,
  // subtaskAssignment,
  triggerShortcut,
  triggerShortcutValue,
  onAuditUpdate,
  user,
  setIsWritingFeedback,
}) => {
  // Need to know the project (for error types), the subtask + attempt ID (as a key for fetching defaults),
  // and the AttemptAuditID (so we know where to save dat

  const [previousResult, setPreviousResult] = useState<
    AuditResultV2 | null | AuditResultV2Old | undefined
  >();
  const [result, setResult] = useState<AuditResultV2 | null | undefined>();
  const [errorCategories, setErrorCategories] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const [feedback, setFeedback] = useState<string>('');

  const onLoadPreviousFeedback = React.useCallback((result: AuditGradeV2) => {
    setPreviousResult(result.result);
    setResult(null);
    setErrorCategories(result.errorCategories ?? []);
    setFeedback(result.feedback ?? '');
    setDirty(false);
  }, []);

  const { updateSpeedAuditFeedback, previousAuditGradeLoading } = useSpeedAuditFeedback({
    attemptAuditId,
    objectId,
    attemptId,
    onLoadPreviousFeedback,
    onAuditUpdate,
  });

  const [updateInProgress, setUpdateInProgress] = useState(false);

  const debouncedUpdateSpeedAuditFeedback = _.debounce(params => {
    updateSpeedAuditFeedback(params)
      .then(() => {
        setUpdateInProgress(false);
        return;
      })
      .catch(() => {
        setUpdateInProgress(false);
        return;
      });
  }, 2000);

  useEffect(() => {
    if (!dirty || updateInProgress) return;
    setUpdateInProgress(true);
    if (previousAuditGradeLoading) {
      // Don't save if we haven't loaded the previous feedback yet.
      return;
    }
    if (!result && !errorCategories) return;

    // Any other changes can be traced to the user and should be saved
    debouncedUpdateSpeedAuditFeedback({
      resultV2: {
        attemptId,
        errorCategories,
        result: result ?? AuditResultV2.Ok,
        feedback,
      },
    });
  }, [
    dirty,
    previousAuditGradeLoading,
    attemptId,
    debouncedUpdateSpeedAuditFeedback,
    errorCategories,
    result,
    feedback,
    updateInProgress,
  ]);

  useEffect(() => {
    if (triggerShortcut && triggerShortcutValue) {
      updateGrade(triggerShortcutValue);
    }
  }, [triggerShortcut, triggerShortcutValue]);

  const toggleChoose = useCallback(
    (newSelected: string[], setVsFilter?: boolean) => {
      const newErrorCategories: string[] = setVsFilter
        ? newSelected
        : _.difference(errorCategories, newSelected);

      setDirty(true);
      setErrorCategories(newErrorCategories);
    },
    [errorCategories, setErrorCategories],
  );

  const updateGrade = (grade: AuditResultV2 | null) => {
    if (!grade) return;
    setDirty(true);
    setResult(grade);
  };

  const updateFeedback = (newFeedback: string) => {
    setFeedback(newFeedback);
    setDirty(true);
  };

  // const [showMoveDialog, setShowMoveDialog] = useState(false);

  // const formatReviewLevel = (reviewLevel?: ReviewLevel) => {
  //   if (typeof reviewLevel !== 'number') {
  //     return reviewLevel ?? 'N/A';
  //   }
  //   if (reviewLevel === ReviewLevel.Attempt) return 'Attempt';
  //   return 'L' + reviewLevel;
  // };

  if (previousAuditGradeLoading) return <Spinner />;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
      <div className="scaleui scale-tailwind scale-theme">
        <SpeedAuditV2ResultToggle value={result ?? null} onChange={updateGrade} />
      </div>
      <div>(Previous Grade: {previousResult || 'None'})</div>
      {errorCategories && (
        <div>
          <SpeedAuditErrorPicker
            enableCreateNewError={!!(user && isScaleEmail(user.email))}
            selectedErrorCategories={errorCategories}
            projectId={projectId}
            toggleChoose={toggleChoose}
          />
        </div>
      )}
      <div style={{ marginLeft: '20px', minWidth: '15%' }}>
        <TextInput
          style={{ width: '100%' }}
          placeholder="Attempt Feedback"
          multiline
          value={feedback}
          maxRows={4}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            updateFeedback(event.target.value);
          }}
          onFocus={() => setIsWritingFeedback && setIsWritingFeedback(true)}
          onBlur={() => setIsWritingFeedback && setIsWritingFeedback(false)}
        />
      </div>
      {/* subtaskAssignment &&
        subtaskAssignment.subtask.status === 'pending' &&
        subtaskAssignment.subtask._id && (
          <div>
            <Button type="primary" variant="contained" onClick={() => setShowMoveDialog(true)}>
              Change Review Level: {formatReviewLevel(subtaskAssignment.subtask.reviewLevel)}
            </Button>
            {showMoveDialog && (
              <Dialog open={showMoveDialog} onClose={() => setShowMoveDialog(false)}>
                <DialogTitle>
                  Change Review Level for Subtask {subtaskAssignment.subtask._id}
                </DialogTitle>
                <DialogContent>
                  <Alert severity={'info'}>
                    Note: You may need to reload the page to see the review level change reflected
                    in Speed Audit.
                  </Alert>
                  <RecallSubtasksNeedsCorpPage
                    subtasks={[subtaskAssignment.subtask._id]}
                    defaultReviewLevel={subtaskAssignment.subtask.reviewLevel}
                  />
                </DialogContent>
              </Dialog>
            )}
          </div>
            ) */}
      {user && (
        <StellarTaskDialog
          feedback={feedback}
          setFeedback={updateFeedback}
          taskAttemptId={attemptId}
          reviewedByUserId={user._id}
        />
      )}
    </div>
  );
};

const SpeedAuditV2ResultToggle: React.FC<{
  value: AuditResultV2 | null;
  onChange: (value: AuditResultV2 | null) => void;
}> = props => {
  const { value, onChange } = props;
  return (
    <SegmentedButtons
      selected={value ?? ''}
      onSelect={(value: string) => onChange(value as AuditResultV2)}
      buttons={[
        { id: AuditResultV2.Terrible, label: '1 Terrible' },
        { id: AuditResultV2.Bad, label: '2 Bad' },
        { id: AuditResultV2.Ok, label: '3 Ok' },
        { id: AuditResultV2.Good, label: '4 Good' },
        { id: AuditResultV2.Perfect, label: '5 Perfect' },
      ]}
    />
  );
};

export default SpeedAuditFeedback;
